import React from 'react'

import {
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react'
import {
  Grid,
  Typography,
  Box,
  Stack,
  Skeleton,
  Button,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material'

import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import DashboardLayout from 'components/LayoutContainers/DashboardLayout'

import Card from 'atoms/Card'
import Arrow from 'atoms/Arrow'

import { Masonry } from '@mui/lab'

import image from 'assets/images/tspPlaceholder.png'
import RiskProfile from '../tsp/components/riskprofile'
import AppointmentScheduler from 'components/AppointmentScheduler'
import Banner from 'atoms/Banner.js'
import networth from 'assets/images/newnetworth.png'
import gapUnlock from 'assets/images/gapUnlock.png'
import retirementScorePlaceholder from 'assets/images/parcoScoreUnlock.png'
import meeting from 'assets/images/icons/meeting-background.svg'
import AssetsTable from 'atoms/AssetsTable'
import RetirementGapTable from './components/RetirementGapTable'
import NetWorthTable from './components/NetWorthTable'
import NetWorthGraph from './components/NetWorthGraph'

import { motion } from 'framer-motion'
import RetirementReadiness from './components/RetirementReadiness'
import RetirementSlider from './components/RetirementSlider'
import ToDoList from './components/ToDoList'
import ScoreCard from 'components/ScoreCard'

import { IntakeContext } from 'context/IntakeContext'
import { apiClient } from 'utils/api/apiClient'

import { useDashboardCalculations } from '../dashboard/hooks/useDashboardCalculations'
import { useRetirementGapCalculations } from '../dashboard/hooks/useRetirementGapCalculations'
import { usePARCOScoreCalculations } from '../dashboard/hooks/usePARCOScoreCalculations'
import { useFormDebounce } from 'hooks/useFormDebounce'
import { useAutoUpdateScores } from 'layouts/authenticated/pages/dashboard/hooks/useAutoUpdateScores'
import { UpdatedCalcs } from 'admin/UpdatedCalcs'
import { useModal } from 'components/modals'
import { steps } from 'layouts/onboarding'
import { callZapier } from 'utils/zapier/callZapier'
import { differenceInYears, addDays } from 'date-fns'
import { SessionInformation } from 'admin/utils/SessionInformation'
import RetirementSolverBanner from '../newprofile/components/RetirementSolverBanner'
import { CatchingPokemonSharp } from '@mui/icons-material'

// Quiltt
import QuilttConnectProvider from './components/QuilttConnectProvider'
import { useQuilttIntegration } from 'hooks/useQuilttIntegration'
import { QuilttButton } from '@quiltt/react'
import { QuilttLogo } from 'components/QuilttLogo'
import TextBanner from 'components/DashboardNavbar/textBanner'

const Dashboard = () => {
  const {
    intakeState,
    updateIntakeStateAtKey,
    updateUser,
    userTasks,
    currentErrors,
    isAdmin,
  } = useContext(IntakeContext)
  const { updateIntakeStateAndSubmit } = useFormDebounce()
  const [greeting, setGreeting] = useState('Good Morning')

  useEffect(() => {
    const getGreeting = () => {
      const hour = new Date().getHours()
      if (hour < 12) return 'Good Morning'
      if (hour < 18) return 'Good Afternoon'
      return 'Good Evening'
    }

    setGreeting(getGreeting())
  }, [])

  const dollarFormat = Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })
  const dollarCentsFormat = Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  })

  const { openModal } = useModal()

  const [assets, setAssets] = useState([])
  const [liabilities, setLiabilities] = useState([])
  const [deductions, setDeductions] = useState([])
  const [incomes, setIncomes] = useState([])
  const [expenses, setExpenses] = useState([])

  const [assetsLoaded, setAssetsLoaded] = useState(false)

  const getAccounts = useCallback(async () => {
    try {
      const {
        data: { items },
      } = await apiClient.get('api/assets')

      const foundAssets =
        items?.filter(
          ({ type }) => type === 'asset' || type === 'assetIncome'
        ) || []
      setAssets(foundAssets)

      const foundLiabilities =
        items?.filter(({ type }) => type === 'liability') || []
      setLiabilities(foundLiabilities)

      const foundDeductions =
        items?.filter(({ type }) => type === 'deduction') || []
      setDeductions(foundDeductions)

      const foundIncomes =
        items?.filter(
          ({ type }) => type === 'income' || type === 'assetIncome'
        ) || []
      setIncomes(foundIncomes)

      const foundExpenses =
        items?.filter(
          ({ type }) => type === 'expense' || type === 'liability'
        ) || []
      setExpenses(foundExpenses)

      setAssetsLoaded(true)
    } catch (error) {
      console.error('Failed to fetch accounts', error)
      setAssetsLoaded(false)
    }
  }, [])

  useEffect(() => {
    getAccounts()
  }, [getAccounts])

  const { valueOfAssets, valueOfInvestableAssets, readinessScoreAdjustedMax } =
    UpdatedCalcs({
      assets,
      liabilities,
    })

  const {
    daysTillRetirement,
    daysSinceRetirement,
    yearsTillRetirement,
    remainingDaysTillRetirement,
    plannedRetirementMonth,
    plannedRetirementDay,
    plannedRetirementYear,
    firstFullyEligibleDate,
  } = useDashboardCalculations(deductions, liabilities)
  const {
    adjustedNetTakeHomeInRetirement,
    adjustedWorkingNetTakeHome,
    adjustedNetTakeHomeDifferencePARCOGap,
    workingIncomesExpenses,
    retirementIncomesExpenses,
  } = useRetirementGapCalculations(deductions, liabilities, incomes, expenses)
  const {
    parcoReadinessScore4Percent,
    parcoReadinessScore3Percent,
    parcoAnnuityRecommendation,
    parcoAnnuityRecommendationCalculator,
  } = usePARCOScoreCalculations(
    assets,
    liabilities,
    adjustedWorkingNetTakeHome,
    adjustedNetTakeHomeInRetirement,
    adjustedNetTakeHomeDifferencePARCOGap
  )

  const retirementLifestyleGoal =
    intakeState?.retirementLifestyleGoal?.lifestyleGoal || 1
  // adjust this to use 0 as the gap when there is a surplus for retirement - otherwise use the calc

  // console.log(adjustedNetTakeHomeDifferencePARCOGap)
  const lifeStyleAdjustedGap =
    adjustedNetTakeHomeDifferencePARCOGap < 0
      ? 0
      : adjustedNetTakeHomeDifferencePARCOGap -
        (1 - retirementLifestyleGoal) * adjustedWorkingNetTakeHome
  // console.log(lifeStyleAdjustedGap)
  const parcoAnnuityRecommendationBasedOnLifestyle =
    parcoAnnuityRecommendationCalculator({
      dateOfBirth: intakeState?.dateOfBirth,
      plannedRetirementDate: intakeState?.dateOfPlannedRetirement,
      netIncomeGap: lifeStyleAdjustedGap < 0 ? 0 : lifeStyleAdjustedGap,
    })

  const neededForRetirementFull = dollarFormat.format(
    parcoAnnuityRecommendationBasedOnLifestyle.threePercent
  )
  const neededForRetirementNumber =
    parcoAnnuityRecommendationBasedOnLifestyle.threePercent
  const neededForRetirementTrimmed =
    parcoAnnuityRecommendationBasedOnLifestyle.threePercent > 999999
      ? `${dollarCentsFormat.format(
          parcoAnnuityRecommendationBasedOnLifestyle.threePercent / 1000000
        )}M`
      : `${dollarFormat.format(
          parcoAnnuityRecommendationBasedOnLifestyle.threePercent / 1000
        )}k`
  const neededForRetirementParco =
    parcoAnnuityRecommendationBasedOnLifestyle.cost > 999999
      ? `${dollarCentsFormat.format(
          parcoAnnuityRecommendationBasedOnLifestyle.cost / 1000000
        )}M`
      : `${dollarFormat.format(
          parcoAnnuityRecommendationBasedOnLifestyle.cost / 1000
        )}k`

  const hasThriftSavingsPlan = assets?.some(
    (item) =>
      item.externalAsset === true &&
      item.externalInstitutionName === 'Thrift Savings Plan'
  )

  const assetSum =
    (assets || []).map((assets) => assets.amount).reduce((a, b) => a + b, 0) +
    (hasThriftSavingsPlan
      ? 0
      : (intakeState.tspBalance ? intakeState.tspBalance : 0) +
        (intakeState.tspRothBalance ? intakeState.tspRothBalance : 0))

  const liabilitySum = (liabilities || [])
    .map((liabilities) => liabilities.amount)
    .reduce((a, b) => a + b, 0)

  const netWorth = assetSum - liabilitySum

  const hasRothIRA = assets?.some(
    (item) => item.subTypeClassification === 'ira-roth'
  )

  const isGovernmentEmployee = intakeState?.governmentEmployee

  // Profile Completion Logic
  const profileCompletionArray = isGovernmentEmployee
    ? [
        'firstName',
        'lastName',
        'email',
        'phone',
        'zipcode',
        'dateOfBirth',
        'dateOfHire',
        'dateOfPlannedRetirement',
        'agency',
        'employmentStatus',
        'baseSalary',
        'highThree',
        'netTakeHome',
        'currentLeaveBalance',
        'currentSickLeaveBalance',
        'estimatedSocialSecurity',
        'retirementSystem',
        'tsp',
        // 'tspBalance',
        // 'tspContribution',
        'tspLoans',
        'tspRoth',
        // 'tspRothBalance',
        // 'tspRothContribution',
        'familyMaritalStatus',
        'military',
        'healthInsurance',
        'healthInsurancePremium',
        'fehbCode',
        'fegli',
        'lifeInsuranceOther',
        'hasTrust',
        'hasWill',
        'retirementLifestyleGoal',
      ]
    : [
        'firstName',
        'lastName',
        'email',
        'phone',
        'zipcode',
        'dateOfBirth',
        'dateOfPlannedRetirement',
        'employmentStatus',
        'employerName',
        'baseSalary',
        'netTakeHome',
        'estimatedSocialSecurity',
        'familyMaritalStatus',
        'lifeInsuranceOther',
        'hasTrust',
        'hasWill',
        'retirementLifestyleGoal',
      ]
  // const missingFields = profileCompletionArray.filter(
  //   (field) => !(field in intakeState)
  // )
  const missingFields = profileCompletionArray.filter((field) => {
    const value = intakeState[field]
    return value === undefined || value === '' || value === 0
  })
  const profileCompletenessFromArrayValue = Math.round(
    ((profileCompletionArray.length - missingFields.length) /
      profileCompletionArray.length) *
      100
  )
  // console.log(
  //   'profileCompletenessFromArrayValue',
  //   profileCompletenessFromArrayValue
  // )
  // Incomplete Questions
  // console.log('allSteps', steps)
  const remainingFullOnboardingSteps = useMemo(
    () =>
      steps.filter(
        (step) =>
          step.tags?.includes('onboarding') &&
          missingFields.some((field) => step.id.startsWith(field))
      ),
    [missingFields]
  )

  const remainingGapSteps = useRef(
    steps.filter((step) => step.tags?.includes('incomeStatement'))
  )

  const remainingScoreSteps = useRef(
    steps.filter((step) => step.tags?.includes('payStubDetails'))
  )

  const clientAge = intakeState?.dateOfBirth
    ? differenceInYears(new Date(), new Date(intakeState?.dateOfBirth))
    : 'Date of Birth Not Provided'

  const retirementSolverClick = () => {
    callZapier({
      user: intakeState,
      hookUrl: 'https://hooks.zapier.com/hooks/catch/15607509/2cdwbv4/',
      payload: {
        user: {
          firstName: intakeState?.firstName,
          lastName: intakeState?.lastName,
          phoneNumber: intakeState?.phone,
          email: intakeState?.email,
          score: isNaN(parcoReadinessScore4Percent)
            ? 0
            : parcoReadinessScore4Percent,
          gap: isNaN(adjustedNetTakeHomeDifferencePARCOGap * 12)
            ? 0
            : adjustedNetTakeHomeDifferencePARCOGap * 12,
          investableAssets: isNaN(valueOfInvestableAssets)
            ? 0
            : valueOfInvestableAssets,
          age: clientAge,
          neededForRetirementFull: neededForRetirementFull,
          neededForRetirementTrimmed: neededForRetirementTrimmed,
          neededForRetirementParco: neededForRetirementParco,
          parcoAnnuityRecommendationBasedOnLifestyle:
            parcoAnnuityRecommendationBasedOnLifestyle,
        },
      },
      sendNotification: false,
    })
    updateIntakeStateAndSubmit(() =>
      updateIntakeStateAtKey({
        clickedRetirementSolver: true,
      })
    )()
  }

  useAutoUpdateScores({
    intakeState,
    updateIntakeStateAndSubmit,
    updateIntakeStateAtKey,
    assets,
    liabilities,
    deductions,
  })

  let sliderDateArray = []

  const riskStatusValue = Math.abs(
    valueOfInvestableAssets /
      (parcoAnnuityRecommendation?.roundedCurrentAge >= 70
        ? parcoAnnuityRecommendation.fourPercent
        : parcoAnnuityRecommendation.threePercent) || 0
  )

  useEffect(() => {
    const maybeOpenFromEngagement = async () => {
      if (!intakeState?.engagementCode) return
      if (intakeState?.engagementModalShown) return

      openModal('lifestyleGoal', {
        adjustedWorkingNetTakeHome,
        adjustedNetTakeHomeInRetirement,
        adjustedNetTakeHomeDifferencePARCOGap,
        valueOfInvestableAssets,
        parcoAnnuityRecommendationCalculator,
      })

      updateIntakeStateAndSubmit(() =>
        updateIntakeStateAtKey({
          engagementModalShown: true,
        })
      )()
    }

    maybeOpenFromEngagement()
  }, [intakeState?.engagementCode])

  const retiredClient = intakeState?.employmentStatus === 'retired'
  // this is causing an issue with hiding the slider if someone slides the date to before today
  //  ||
  // new Date(intakeState?.dateOfPlannedRetirement || new Date()) < new Date()

  // mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1400)
  const [isMobileHeader, setIsMobileHeader] = useState(window.innerWidth < 600)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1400)
      setIsMobileHeader(window.innerWidth < 600)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const { refreshQuiltt, handleExitSuccess } = useQuilttIntegration(() => {
    console.log('Accounts updated!')
    getAccounts()
  })

  // console.log('remainingFullOnboardingSteps', remainingFullOnboardingSteps)

  const toDoListItems = [
    {
      header: 'Complete Onboarding',
      completed: profileCompletenessFromArrayValue <= 70 ? false : true,
      onClick: () => {
        profileCompletenessFromArrayValue <= 70
          ? openModal('questionModal', {
              modalHeader: 'Complete Onboarding',
              steps: remainingFullOnboardingSteps,
              completionStepProps: {
                header: 'Onboarding Complete!',
                subHeader:
                  'Head back to the dashboard to unlock more tiles or complete To-Do items!',
              },
            })
          : openModal('fallbackModal', {
              title: 'Onboarding Complete!',
              content:
                'You have already completed your onboarding! Knock out another item on your list or explore other areas of the App.',
              showAction: false,
            })
      },
    },
    {
      header: 'Contribute 5% to the TSP',
      completed:
        parseInt(
          intakeState?.tspContribution ||
            0 + intakeState?.tspRothContribution ||
            0
        ) >= 5,
      onClick: () => {
        parseInt(
          intakeState?.tspContribution ||
            0 + intakeState?.tspRothContribution ||
            0
        ) < 5
          ? openModal('contributeTSP', {
              openTo: 3,
              currentContribution:
                intakeState?.tspContribution ||
                0 + intakeState?.tspRothContribution ||
                0,
            })
          : openModal('fallbackModal', {
              title: 'Contribute 5% to the TSP Complete!',
              content:
                'You already contribute ' +
                parseInt(
                  intakeState?.tspContribution ||
                    0 + intakeState?.tspRothContribution ||
                    0
                ) +
                '% to the TSP! Knock out another item on your list or explore other areas of the App.',
              showAction: false,
            })
      },
    },
    {
      header: 'Complete Risk Assessment',
      completed: intakeState?.riskAssessment?.isComplete,
      onClick: () => {
        intakeState?.riskAssessment?.isComplete
          ? openModal('fallbackModal', {
              title: 'Risk Assessment Complete!',
              content:
                'Knock out another item on your list or explore other areas of the App.',
              showAction: false,
            })
          : openModal('riskAssessmentQuiz', {})
      },
    },

    {
      header: 'Set your Lifestyle Goal',
      completed: intakeState?.retirementLifestyleGoal?.lifestyleGoal,

      onClick: () => {
        intakeState?.retirementLifestyleGoal?.lifestyleGoal
          ? openModal('fallbackModal', {
              title: 'Lifestyle Goal Complete!',
              content:
                'Knock out another item on your list or explore other areas of the App.',
              showAction: false,
            })
          : openModal('lifestyleGoal', {
              adjustedWorkingNetTakeHome: adjustedWorkingNetTakeHome,
              adjustedNetTakeHomeInRetirement: adjustedNetTakeHomeInRetirement,
              adjustedNetTakeHomeDifferencePARCOGap:
                adjustedNetTakeHomeDifferencePARCOGap,
              valueOfInvestableAssets: valueOfInvestableAssets,
              parcoAnnuityRecommendationCalculator:
                parcoAnnuityRecommendationCalculator,
            })
      },
    },
    // {
    //   header:
    //     'You are earning less with the G fund! Check out an alternative',

    //   //make completed when appointment scheduled
    //   completed: false,
    //   onClick: () => {
    //     openModal('contributeTSP', {
    //       openTo: 5,
    //       currentContribution:
    //         intakeState.tspContribution + intakeState.tspRothContribution,
    //     })
    //   },
    // },
    // {
    //   header: 'Become a PARCO App Tester!',
    //   completed: false,
    //   onClick: () => {
    //     openModal('testerSignup', {})
    //   },
    // },
    {
      header: 'Open a Roth IRA',
      completed: hasRothIRA,
      onClick: () =>
        hasRothIRA
          ? openModal('fallbackModal', {
              title: 'Open a Roth IRA Complete!',
              content:
                'You already have a Roth IRA! Knock out another item on your list or explore other areas of the App.',
              showAction: false,
            })
          : openModal('openRothIRA', {
              title: 'Open a Roth IRA',
              content:
                'The PARCO team is here to help! Schedule a meeting and one of our teammates will walk you through the process.',
              intakeState: intakeState,
            }),
    },
    {
      header: 'Set up a Will',
      completed: intakeState?.hasWill ? true : false,
      onClick: () =>
        openModal('willSimple', {
          updateIntakeStateAndSubmit: updateIntakeStateAndSubmit,
          updateIntakeStateAtKey: updateIntakeStateAtKey,
        }),
    },
  ]

  const filteredForRetirementToDoItems = toDoListItems.filter((item) => {
    if (retiredClient && item.header === 'Contribute 5% to the TSP')
      return false
    if (retiredClient && item.header === 'Open a Roth IRA') return false
    if (
      profileCompletenessFromArrayValue >= 70 &&
      item.header === 'Complete Onboarding'
    )
      return false
    if (
      parseInt(intakeState.tspContribution + intakeState.tspRothContribution) >=
        5 &&
      item.header === 'Contribute 5% to the TSP'
    )
      return false
    if (!isGovernmentEmployee && item.header === 'Contribute 5% to the TSP')
      return false
    return true
  })

  const tiles = [
    // Lifestyle Lock / Unlock Tile

    <>
      {profileCompletenessFromArrayValue <= 70 && (
        <motion.div
          key={7}
          // style={{ minWidth: '500px' }}
          layout
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
          style={{ marginBottom: '40px' }}
        >
          <Banner
            color={''}
            type={'unlock'}
            actionText="FINISH ONBOARDING"
            clickAction={() => {
              openModal('questionModal', {
                modalHeader: 'Complete Onboarding',
                steps: remainingFullOnboardingSteps,
                completionStepProps: {
                  header: 'Onboarding Complete!',
                  subHeader:
                    'Head back to the dashboard to unlock more tiles or complete To-Do items!',
                },
              })
            }}
            title="PROFILE COMPLETION"
            description={`${profileCompletenessFromArrayValue}%`}
            subText=""
            content={<></>}
          />
        </motion.div>
      )}
      {intakeState?.retirementLifestyleGoal?.lifestyleGoal ? (
        <motion.div
          key={0}
          layout={false}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
        >
          <RetirementSolverBanner
            clickAction={() => {
              retirementSolverClick()
              openModal('retirementSolver', {
                neededForRetirementFull: neededForRetirementFull,
                neededForRetirementTrimmed: neededForRetirementTrimmed,
                neededForRetirementParco: neededForRetirementParco,
              })
            }}
            parcoAnnuityRecommendationBasedOnLifestyle={
              parcoAnnuityRecommendationBasedOnLifestyle
            }
            neededForRetirementNumber={neededForRetirementNumber}
            neededForRetirementFull={neededForRetirementFull}
            neededForRetirementTrimmed={neededForRetirementTrimmed}
            neededForRetirementParco={neededForRetirementParco}
          />
        </motion.div>
      ) : (
        <motion.div
          key={0}
          // style={{ minWidth: '500px' }}
          layout
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
        >
          <Banner
            color={''}
            type={'unlock'}
            actionText="SET MY LIFESTYLE GOAL"
            clickAction={() => {
              openModal('lifestyleGoal', {
                adjustedWorkingNetTakeHome: adjustedWorkingNetTakeHome,
                adjustedNetTakeHomeInRetirement:
                  adjustedNetTakeHomeInRetirement,
                adjustedNetTakeHomeDifferencePARCOGap:
                  adjustedNetTakeHomeDifferencePARCOGap,
                valueOfInvestableAssets: valueOfInvestableAssets,
                parcoAnnuityRecommendationCalculator:
                  parcoAnnuityRecommendationCalculator,
              })
            }}
            title="UNLOCK TILE"
            description="Retirement Lifestyle Goal"
            subText=""
            content=""
          />
        </motion.div>
      )}
    </>,
    // Net Worth Lock / Unlock Tile
    <>
      {assetsLoaded ? (
        <>
          {assets.length + liabilities.length > 0 ||
          (intakeState?.tspBalance || 0) + (intakeState?.tspRothBalance || 0) >
            0 ? (
            <motion.div
              key={1}
              layout
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: 'easeInOut' }}
            >
              <Card
                header={
                  <Banner
                    color={'white'}
                    type={'header'}
                    actionText="ADD / LINK ACCOUNT"
                    clickAction={() => {
                      openModal('addAccounts', {
                        getAccounts: getAccounts,
                        openToPage: 0,
                      })
                    }}
                    title="MY NET WORTH"
                    description={netWorth}
                    formatDescription
                    counterDescription
                    subText="up"
                    content={<NetWorthGraph netWorth={netWorth} />}
                  />
                }
              >
                <NetWorthTable
                  getAccounts={getAccounts}
                  assets={assets}
                  liabilities={liabilities}
                />
              </Card>
            </motion.div>
          ) : (
            <motion.div
              key={1}
              // style={{ minWidth: '500px' }}
              layout
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: 'easeInOut' }}
            >
              <Banner
                color={''}
                type={'unlock'}
                actionText="SEE YOUR NET WORTH"
                clickAction={() => {
                  openModal('addAccounts', {
                    getAccounts: getAccounts,
                    openToPage: 0,
                  })
                }}
                title="UNLOCK TILE"
                description="Net Worth"
                subText=""
                content={
                  <>
                    <img
                      style={{
                        objectFit: 'cover',
                        width: '250px',
                        marginTop: '64px',
                        boxShadow: '0px 4px 25px -8px #00000033',
                      }}
                      src={networth}
                    />
                  </>
                }
              />
            </motion.div>
          )}
        </>
      ) : (
        <motion.div
          key={1}
          // style={{ minWidth: '500px' }}
          layout
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
        >
          <Skeleton variant="rounded" height={200} animation={'wave'} />
        </motion.div>
      )}
    </>,
    // Retirement Gap Lock/Unlock Tile
    // REPLACE WITH PERCENTAGE with PERCENTAGE CHECK
    <>
      {intakeState?.dateOfBirth != null &&
      intakeState?.dateOfHire != null &&
      intakeState?.dateOfPlannedRetirement != null &&
      intakeState?.baseSalary != null &&
      intakeState?.highThree != null ? (
        <motion.div
          key={2}
          // style={{ minWidth: '500px' }}
          layout
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
        >
          <RetirementGapTable
            parcoScore={parcoReadinessScore4Percent}
            retirementGap={Math.abs(adjustedNetTakeHomeDifferencePARCOGap)}
            intakeState={intakeState}
            deductions={deductions}
            liabilities={liabilities}
            incomes={incomes}
            expenses={expenses}
            getAccounts={getAccounts}
            dashboardVersion={
              intakeState?.retirementLifestyleGoal?.lifestyleGoal ? true : false
            }
            retirementLifestyleGoal={
              intakeState?.retirementLifestyleGoal?.lifestyleGoal || 1
            }
            lifeStyleClickAction={() => {
              openModal('lifestyleGoal', {
                adjustedWorkingNetTakeHome: adjustedWorkingNetTakeHome,
                adjustedNetTakeHomeInRetirement:
                  adjustedNetTakeHomeInRetirement,
                adjustedNetTakeHomeDifferencePARCOGap:
                  adjustedNetTakeHomeDifferencePARCOGap,
                valueOfInvestableAssets: valueOfInvestableAssets,
                parcoAnnuityRecommendationCalculator:
                  parcoAnnuityRecommendationCalculator,
              })
            }}
            lifeStyleStyling={true}
          />
          {/* <Card
            header={
              <Banner
                bannerID={'retirementGap-header'}
                color={
                  adjustedNetTakeHomeDifferencePARCOGap < 0 ? 'green' : 'red'
                }
                type={'header'}
                actionText="BRIDGE MY GAP"
                clickAction={() => {
                  retirementSolverClick()
                  openModal('annuityRecommender', {
                    parcoReadinessScore4Percent: parcoReadinessScore4Percent,
                    adjustedNetTakeHomeDifferencePARCOGap:
                      adjustedNetTakeHomeDifferencePARCOGap,
                    valueOfInvestableAssets: valueOfInvestableAssets,
                    parcoAnnuityRecommendation: parcoAnnuityRecommendation,
                  })
                }}
                title={
                  adjustedNetTakeHomeDifferencePARCOGap < 0
                    ? 'MY RETIREMENT SURPLUS'
                    : 'MY RETIREMENT GAP'
                }
                description={Math.abs(adjustedNetTakeHomeDifferencePARCOGap)}
                counterDescription
                subText="/month"
              />
            }
          >
            <RetirementGapTable
              parcoScore={parcoReadinessScore4Percent}
              retirementGap={Math.abs(adjustedNetTakeHomeDifferencePARCOGap)}
              intakeState={intakeState}
              deductions={deductions}
              liabilities={liabilities}
              incomes={incomes}
              expenses={expenses}
            />
          </Card> */}
        </motion.div>
      ) : (
        <motion.div
          key={2}
          // style={{ minWidth: '500px' }}
          layout
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
        >
          <Banner
            color={''}
            type={'unlock'}
            actionText="SEE YOUR INCOME GAP"
            clickAction={() => {
              openModal('questionModal', {
                modalHeader: 'Unlock Retirement Gap',
                steps: remainingGapSteps.current,
                completionStepProps: {
                  header: 'Retirement Gap Unlocked!',
                  subHeader:
                    'Head back to the dashboard to unlock more tiles or complete To-Do items!',
                },
              })
            }}
            title="UNLOCK TILE"
            description="Retirement Gap"
            subText=""
            content={
              <>
                <img
                  style={{
                    objectFit: 'cover',
                    width: '250px',
                    marginTop: '64px',

                    boxShadow: '0px 4px 25px -8px #00000033',
                  }}
                  src={gapUnlock}
                />
              </>
            }
          />
        </motion.div>
      )}
    </>,
    <>
      {intakeState?.baseSalary != null &&
      intakeState?.highThree != null &&
      // REMOVE
      // intakeState?.currentSickLeaveBalance != null &&
      !isNaN(parcoReadinessScore4Percent) ? (
        <ScoreCard
          key={3}
          value={Math.round(
            (clientAge >= 70
              ? parcoReadinessScore4Percent
              : parcoReadinessScore3Percent) * 100
          )}
          goal={`${plannedRetirementMonth} ${plannedRetirementYear}`}
          type="retirement"
        />
      ) : (
        <motion.div
          key={3}
          // style={{ minWidth: '500px' }}
          layout
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
        >
          <Banner
            color={''}
            type={'unlock'}
            actionText="SEE YOUR SCORE"
            clickAction={() => {
              openModal('questionModal', {
                modalHeader: 'Unlock PARCO Retirement Score',
                steps: remainingScoreSteps.current,
                completionStepProps: {
                  header: 'PARCO Retirement Score Unlocked!',
                  subHeader:
                    'Head back to the dashboard to unlock more tiles or complete To-Do items!',
                },
              })
            }}
            title="UNLOCK TILE"
            description="Retirement Score"
            subText=""
            content={
              <>
                <img
                  style={{
                    objectFit: 'cover',
                    width: '250px',
                    marginTop: '32px',
                    boxShadow: '0px 4px 25px -8px #00000033',
                  }}
                  src={retirementScorePlaceholder}
                />
              </>
            }
          />
        </motion.div>
      )}
    </>,
    <>
      <ToDoList key={4} items={filteredForRetirementToDoItems} />
    </>,

    // <>
    //   {new Date(intakeState?.dateOfPlannedRetirement) < new Date() ? (
    //     <></>
    //   ) : (
    //     <RetirementSlider key={6} />
    //   )}
    // </>,

    <>
      {!retiredClient &&
        intakeState?.dateOfBirth != null &&
        intakeState?.dateOfPlannedRetirement != null && (
          <RetirementSlider key={5} sliderDateArray={sliderDateArray} />
        )}
    </>,

    // <RetirementSlider key={6} sliderDateArray={sliderDateArray} />,

    // <motion.div
    //   key={7}
    //   layout
    //   initial={{ opacity: 0, scale: 0.95 }}
    //   animate={{ opacity: 1, scale: 1 }}
    //   transition={{ duration: 0.4, ease: 'easeInOut' }}
    // >
    //   <Banner
    //     color={'yellow'}
    //     actionText="SEE YOUR NETWORTH"
    //     title="ATTENTION REQUIRED"
    //     description="You are working for $6 an hour "
    //     subText=""
    //   />
    // </motion.div>,

    <motion.div
      key={6}
      // style={{ minWidth: '500px' }}
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, ease: 'easeInOut' }}
    >
      <Banner
        color={'blue'}
        type={'banner'}
        actionText="TALK TO A STRATEGIST"
        clickAction={() => {
          openModal('scheduleAppointment')
          updateIntakeStateAndSubmit(() =>
            updateIntakeStateAtKey({
              clickedScheduleFreeReview: true,
            })
          )()
        }}
        title=""
        description="Schedule a Free Review"
        subText="Tap to speak with a PARCO team member"
        content={
          <>
            <img
              style={{
                objectFit: 'cover',
                paddingTop: '16px',
                width: '100px',
              }}
              src={meeting}
            />
          </>
        }
      />
    </motion.div>,
    // <motion.div
    //   key={6}
    //   // style={{ minWidth: '500px' }}
    //   layout
    //   initial={{ opacity: 0, scale: 0.95 }}
    //   animate={{ opacity: 1, scale: 1 }}
    //   transition={{ duration: 0.4, ease: 'easeInOut' }}
    // >
    //   <Card>
    //     <Box p={3}>
    //       <Typography>Quiltt Testing</Typography>
    //       <QuilttButton
    //         connectorId="5ugr9q35ag"
    //         institution="Thrift"
    //         onExitSuccess={handleExitSuccess}
    //       >
    //         Connect TSP with <QuilttLogo />
    //       </QuilttButton>
    //       <Button variant="parcoRounded" onClick={refreshQuiltt}>
    //         Refresh Quiltt
    //       </Button>
    //     </Box>
    //   </Card>
    // </motion.div>,
  ]

  function parseLocalDate(input) {
    if (!input) return null
    if (input instanceof Date) {
      // Already a Date, normalize to local midnight
      return new Date(input.getFullYear(), input.getMonth(), input.getDate())
    }
    if (typeof input === 'string') {
      // Parse "YYYY-MM-DD"
      const [year, month, day] = input.split('-').map(Number)
      return new Date(year, month - 1, day)
    }
    throw new Error('parseLocalDate: unsupported input type')
  }
  function formatLocalDate(date) {
    if (!date) return null
    const yyyy = date.getFullYear()
    const mm = String(date.getMonth() + 1).padStart(2, '0')
    const dd = String(date.getDate()).padStart(2, '0')
    return `${yyyy}-${mm}-${dd}`
  }

  const [rifStatus, setRifStatus] = useState(
    intakeState?.rifPlan?.rifStatus || false
  )
  const switchRifStatus = (event, newRifStatus) => {
    newRifStatus === null ? setRifStatus(false) : setRifStatus(newRifStatus)
  }
  const [storedDOPR, setStoredDOPR] = useState(
    intakeState?.rifPlan?.storedDateOfPlannedRetirment
  )
  useEffect(() => {
    if (intakeState?.rifPlan?.storedDateOfPlannedRetirment) {
      setStoredDOPR(intakeState.rifPlan.storedDateOfPlannedRetirment)
    }
  }, [intakeState?.rifPlan?.storedDateOfPlannedRetirment])
  const toggleRifMode = () => {
    setRifStatus(!rifStatus)
    rifStatus
      ? updateIntakeStateAndSubmit(() =>
          updateIntakeStateAtKey({
            retirementType: 'full',
            rifPlan: {
              rifStatus: false,
            },
            dateOfPlannedRetirement: storedDOPR,
          })
        )()
      : updateIntakeStateAndSubmit(() =>
          updateIntakeStateAtKey({
            retirementType: 'dsr',
            rifPlan: {
              rifStatus: true,
              storedDateOfPlannedRetirment:
                intakeState?.dateOfPlannedRetirement,
            },
            dateOfPlannedRetirement: formatLocalDate(addDays(new Date(), 30)),
          })
        )()
  }
  // console.log(intakeState)
  // console.log('initialDOPR', initialDOPR)

  return (
    <>
      <DashboardLayout>
        <TextBanner />
        <QuilttConnectProvider />
        <Box
          px={{
            xs: 2,
            sm: 4,
            md: 4,
            lg: 6,
            xl: 6,
            xxl: 6,
            test: 6,
            xl3: 6,
          }}
          pb={6}
        >
          <Grid
            item
            xs={12}
            mb={4}
            mt={8}
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            flexDirection={{ xs: 'column', md: 'row' }}
          >
            <Box sx={{ textAlign: 'left' }}>
              <Typography
                fontWeight="600"
                fontSize="28px"
                color="#262627"
                display={'inline-block'}
              >
                {intakeState?.firstName
                  ? `${greeting}, ${intakeState.firstName}!`
                  : `${greeting}!`}
              </Typography>
              {rifStatus && (
                <Typography
                  fontWeight="400"
                  fontSize="28px"
                  color="#262627"
                  display={'inline-block'}
                  marginLeft={{ xs: 0, xxl: 1 }}
                >
                  You're currently in RIF Mode:
                </Typography>
              )}
            </Box>

            <Button
              variant="parcoRounded"
              color={rifStatus ? 'secondary' : 'multi'}
              sx={{
                marginLeft: { xs: '0px', md: '8px' },
                marginTop: { xs: 1, md: 0 },
                height: '100%',
              }}
              onClick={toggleRifMode}
            >
              {rifStatus ? 'Exit RIF Mode' : `What if I get RIF'd`}
            </Button>
          </Grid>

          <Box>
            <Box display={{ xs: 'block', xxl: 'none' }}>
              <Stack spacing={{ xs: 4, md: 6 }}>
                {tiles.map((item, i) => (
                  <Box key={i}>{item}</Box>
                ))}
              </Stack>
            </Box>

            <Box display={{ xs: 'none', xxl: 'flex' }} gap={6}>
              <Stack spacing={6} flex={1}>
                {tiles
                  .filter((_, i) => i % 2 === 0)
                  .map((item, i) => (
                    <Box key={`left-${i}`}>{item}</Box>
                  ))}
              </Stack>
              <Stack spacing={6} flex={1}>
                {tiles
                  .filter((_, i) => i % 2 !== 0)
                  .map((item, i) => (
                    <Box key={`right-${i}`}>{item}</Box>
                  ))}
              </Stack>
            </Box>
          </Box>
        </Box>
        {/* </Grid> */}
      </DashboardLayout>
    </>
  )
}

export default Dashboard
